import { event } from "../core/EventUtil.js";
import { merge } from "../core/Merge.js";
import { Signal } from "../core/Signal.js";
import { KeyCode } from "../io/KeyCode.js";
import { TabTrapFocus, tabTrap } from "./TabTrap.js";

export interface ModalOptions {
    /**
     * The content of the modal.
     */
    content: string | HTMLElement | ((info: Modal) => string | HTMLElement);
    /**
     * The title of the modal.
     */
    title?: string;
    /**
     * When closable is false, you need a close button in your content.
     * That button should have the attribute data-modal-action="close".
     */
    closable?: boolean;
    focus?: TabTrapFocus;
    layout?: "full" | "center" | "sidebar-left" | "sidebar-right";
    width?: number | string;
    height?: number | string;
    maxWidth?: number | string;
    maxHeight?: number | string;
    contentPadding?: number | string;
    chromeless?: boolean;
    stackingMode?: "close" | "stack" | "hide";
    /**
     * To add multiple CSS classes, separate them with a space.
     */
    className?: string;

    onInit?: (modal: Modal) => void;
    onDispose?: (modal: Modal) => void;
    onClose?: (modal: Modal) => void;
    onHide?: (modal: Modal) => void;
    onShow?: (modal: Modal) => void;
    onRemove?: (modal: Modal) => void;
}

export class Modal {
    public readonly onDispose = Signal.create<[Modal]>();
    public readonly onClose = Signal.create<[Modal]>();
    public readonly onHide = Signal.create<[Modal]>();
    public readonly onShow = Signal.create<[Modal]>();
    public readonly onRemove = Signal.create<[Modal]>();

    public readonly element: HTMLElement;
    public readonly options: ModalOptions;
    public readonly focusBefore: HTMLElement;
    public readonly uid: string;

    private static _uid: number = 0;

    private _content: HTMLElement;
    private _disposables: DisposableObject[] = [];
    private _removeTimeout: any = null;
    private _closed: boolean = false;

    constructor(options: ModalOptions, focusBefore: HTMLElement) {
        this.focusBefore = focusBefore;
        this.uid = "modal-" + ++Modal._uid + "";
        options = merge(
            {
                closable: true,
                layout: "center",
                title: "",
                width: 500,
                height: "auto",
                maxHeight: "auto",
                maxWidth: "auto",
                contentPadding: "var(--jsidea-global__body__padding, 24px)",
                chromeless: false,
                stackingMode: "hide",
            },
            options
        );

        this.options = options;

        this.element = document.createElement("div");

        if (options.width) {
            const w =
                typeof options.width === "number"
                    ? options.width + "px"
                    : options.width;
            this.element.style.setProperty("--jsidea-modal__box__width", w);
        }

        if (options.height) {
            const h =
                typeof options.height === "number"
                    ? options.height + "px"
                    : options.height;
            this.element.style.setProperty("--jsidea-modal__box__height", h);
        }

        if (options.maxWidth) {
            const w =
                typeof options.maxWidth === "number"
                    ? options.maxWidth + "px"
                    : options.maxWidth;
            this.element.style.setProperty("--jsidea-modal__box__maxWidth", w);
        }

        if (options.maxHeight) {
            const h =
                typeof options.maxHeight === "number"
                    ? options.maxHeight + "px"
                    : options.maxHeight;
            this.element.style.setProperty("--jsidea-modal__box__maxHeight", h);
        }

        if (typeof options.contentPadding === "number") {
            this.element.style.setProperty(
                "--jsidea-modal__content__padding",
                options.contentPadding + "px"
            );
        } else if (options.contentPadding) {
            this.element.style.setProperty(
                "--jsidea-modal__content__padding",
                options.contentPadding
            );
        }

        this.element.className =
            "jsidea-modal jsidea-modal--closed jsidea-modal--layout-" +
            options.layout;
        if (options.className) {
            this.element.classList.add(...options.className.split(" "));
        }
        this.element.setAttribute("role", "dialog");
        this.element.setAttribute("aria-modal", "true");
        this.element.innerHTML = `
        <div class="jsidea-modal__background"></div>
        <div class="jsidea-modal__box">
            <div class="jsidea-modal__toolbar">
                <div class="jsidea-modal__toolbar-title"><span></span></div>
                <div class="jsidea-modal__toolbar-fill"></div>
                <button class="jsidea-modal__toolbar-button" data-modal-action="close">
                    <i class="fal fa-times"></i>
                </button>
            </div>
            <div class="jsidea-modal__content"></div>
        </div>
        `;

        if (options.chromeless) {
            this.element.classList.add("jsidea-modal--chromeless");
        }

        this._content = this.element.querySelector(".jsidea-modal__content");

        this.closable = options.closable;
        if (options.title) {
            this.title = options.title;
        }

        let content: string | HTMLElement = null;
        if (typeof options.content === "function") {
            content = options.content(this);
        } else {
            content = options.content;
        }
        if (typeof content === "string") {
            this._content.innerHTML = content;
        } else {
            this._content.innerHTML = "";
            this._content.appendChild(content);
        }

        let keyRepeat = false;
        let keyRepeatTime = 0;

        this._disposables.push(
            event.on({
                scope: this.element,
                type: "click",
                selector: `[data-modal-action="close"]`,
                listener: (e) => {
                    if (!this.active) {
                        return;
                    }

                    /**
                     * Prevent holding key down triggers multiple
                     * times a click event. When the modal is opened by keyboard (enter, space)
                     * and the user holds the key down, the modal will close immediately when a
                     * close button is focused.
                     */
                    if (keyRepeat && Date.now() - keyRepeatTime < 400) {
                        return;
                    }

                    this.close();
                },
            })
        );

        this._disposables.push(
            event.on({
                scope: document,
                type: "keydown",
                listener: (e) => {
                    if (!this.active) {
                        return;
                    }

                    if (KeyCode.is(e.event, KeyCode.ENTER, KeyCode.SPACE)) {
                        keyRepeat = e.event.repeat;
                        if (keyRepeat) {
                            keyRepeatTime = Date.now();
                        }
                    }

                    if (e.event.repeat) {
                        return;
                    }

                    if (KeyCode.is(e.event, KeyCode.ESCAPE)) {
                        if (this.options.closable) {
                            this.close();
                        }
                    }
                },
            })
        );

        if (typeof options.onDispose === "function") {
            this.onDispose.add(options.onDispose);
        }

        if (typeof options.onClose === "function") {
            this.onClose.add(options.onClose);
        }

        if (typeof options.onHide === "function") {
            this.onHide.add(options.onHide);
        }

        if (typeof options.onShow === "function") {
            this.onShow.add(options.onShow);
        }

        if (typeof options.onRemove === "function") {
            this.onRemove.add(options.onRemove);
        }

        if (typeof options.onInit === "function") {
            options.onInit(this);
        }
    }

    public close(): void {
        if (this._closed) {
            console.warn("Modal.close() called multiple times.");
            return;
        }
        this._closed = true;
        this.onClose.dispatch(this);
    }

    public get active(): boolean {
        return this.element.classList.contains("jsidea-modal--active");
    }

    public set active(value: boolean) {
        this.element.classList.toggle("jsidea-modal--active", value);
    }

    public get title(): string {
        return this.element.querySelector(".jsidea-modal__toolbar-title>span")
            .textContent;
    }

    public set title(value: string) {
        this.element.querySelector(
            ".jsidea-modal__toolbar-title>span"
        ).textContent = value;
    }

    public get closable(): boolean {
        return this.element.classList.contains("jsidea-modal--closable");
    }

    public set closable(value: boolean) {
        this.element.classList.toggle("jsidea-modal--closable", value);
        this.element
            .querySelector(".jsidea-modal__background")
            .setAttribute("data-modal-action", value ? "close" : "none");
    }

    public get visible(): boolean {
        return !this.element.classList.contains("jsidea-modal--closed");
    }

    public set visible(value: boolean) {
        if (this.visible == value) {
            return;
        }
        this.element.classList.toggle("jsidea-modal--closed", !value);

        if (value) {
            document.body.appendChild(this.element);
        } else {
            if (this._removeTimeout) {
                clearTimeout(this._removeTimeout);
            }
            this._removeTimeout = setTimeout(() => {
                if (!this.visible && this.element.parentNode) {
                    this.element.remove();
                    this.onRemove.dispatch(this);
                }
            }, 400);
        }

        if (value) {
            this.onShow.dispatch(this);
        } else {
            this.onHide.dispatch(this);
        }
    }

    public dispose(): void {
        this.onDispose.dispatch(this);

        let m = this as Mutable<this>;
        m.element.remove();
        m.element = null;
        m.options = null;
        m.onRemove.dispose();
        m.onRemove = null;
        m.onHide.dispose();
        m.onHide = null;
        m.onDispose.dispose();
        m.onDispose = null;
        m.onClose.dispose();
        m.onClose = null;
        m.onShow.dispose();
        m.onShow = null;
        m.focusBefore = null;
        this._content = null;

        if (this._removeTimeout) {
            clearTimeout(this._removeTimeout);
            this._removeTimeout = null;
        }

        for (let d of this._disposables) {
            d.dispose();
        }
        this._disposables = null;
    }
}

class ModalManager {
    private _stack: Modal[] = [];
    private _current: Modal = null;
    private _optionsLookup: { [key: string]: ModalOptions } = {};

    constructor() {
        window.addEventListener("popstate", (e) => {
            if (!this._current && e.state && e.state.modalUid) {
                //TODO: Re-open modal from history state
                // const has = this._stack.some((m) =>  m.uid === e.state.modalUid);
                // if(!has){
                //     const options = this._lookup[e.state.modalUid];
                //     if(options){
                //         this.show(options);
                //     }
                // }
            } else if (
                this._current &&
                (!e.state || e.state.modalUid !== this._current.uid)
            ) {
                this._current.close();
            }
        });
    }

    

    public show(options: ModalOptions): Modal {
        const focusBefore = document.activeElement as HTMLElement;
        let modal = new Modal(options, focusBefore);
        this._stack.push(modal);

        // this._optionsLookup[modal.uid] = options;

        modal.onClose.add(() => {
            if (history.state && history.state.modalUid === modal.uid) {
                history.back();
            }

            const index = this._stack.indexOf(modal);
            if (this.current === modal) {
                this.current = this._stack[index - 1] || null;
            }
            this._stack.splice(index, 1);

            setTimeout(() => {
                modal.dispose();
                modal = null;
            }, 400);
        });

        history.pushState({ modalUid: modal.uid }, "", location.href);

        this.current = modal;

        return modal;
    }

    public clearStack(): void {
        this._stack.forEach((m) => m.close());
    }

    private get current(): Modal {
        return this._current;
    }

    private set current(value: Modal) {
        if (this._current === value) {
            return;
        }

        const focusBefore = this._current?.focusBefore;

        if (this._current) {
            const c = this._current;
            c.active = false;
            this._current = null;

            if (c.options.stackingMode === "close") {
                c.visible = false;
                //If modals opens another modal
                if (value) {
                    c.close();
                }
            } else if (c.options.stackingMode === "hide") {
                c.visible = false;
            } else if (c.options.stackingMode === "stack") {
                // Do nothing, keep it visible
                if (value) {
                    console.warn(
                        "Modal stackingMode 'stack' not implemented yet."
                    );
                } else {
                    c.visible = false;
                }
            }

            tabTrap.setElement(null);
        }
        this._current = value;

        if (this._current) {
            this._current.active = true;
            this._current.visible = true;

            tabTrap.setElement(this._current.element);
            if (this._current.options.focus) {
                tabTrap.focus(this._current.options.focus);
            }

            // Handle scrollbar gap, add margin to right equal to scrollbar width
            // If there was already a modal open, no need to calculate or set again.
            if (
                !document.documentElement.classList.contains(
                    "jsidea-modal__root--open"
                )
            ) {
                document.documentElement.style.setProperty(
                    "--jsidea-modal__scrollbarWidth",
                    window.innerWidth -
                        document.documentElement.clientWidth +
                        "px"
                );
                document.documentElement.classList.add(
                    "jsidea-modal__root--open"
                );
            }
        } else {
            setTimeout(() => {
                if (!this._current) {
                    document.documentElement.classList.remove(
                        "jsidea-modal__root--open"
                    );
                }
            }, 400);
        }

        if (!this._current && focusBefore) {
            // console.log("FOCUS BEFORE", focusBefore);
            try {
                focusBefore.focus();
            } catch {}
        }
    }
}

export const modal = new ModalManager();
